import { useState } from "react";
import { AlertTriangle, ShieldCheck, FileText } from "lucide-react";
import { PageHeader } from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.ceil((d - new Date()) / 86400000);
}

function ExpiryRow({ label, value, expiry }) {
  const days = daysUntil(expiry);
  const warn = days !== null && days <= 30;
  return (
    <div className="flex items-center justify-between px-5 py-4">
      <div>
        <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">{label}</div>
        <div className="text-sm text-[#0a0a0a] mt-0.5">{value}</div>
      </div>
      <div className="text-right">
        {expiry ? (
          <>
            <div className={`text-sm ${warn ? "text-orange-600 font-semibold" : "text-[#0a0a0a]"}`}>{new Date(expiry).toLocaleDateString()}</div>
            <div className={`flex items-center justify-end gap-1 text-xs mt-0.5 ${warn ? "text-orange-600" : "text-gray-400"}`}>
              {warn && <AlertTriangle className="h-3 w-3" />}
              {days < 0 ? "Expired" : `${days}d left`}
            </div>
          </>
        ) : <span className="text-xs text-gray-400">—</span>}
      </div>
    </div>
  );
}

export default function DriverDocuments() {
  const { user } = useAuth();
  const c = user?.compliance || {};
  const [viewing, setViewing] = useState("");

  const viewDoc = async (fileId) => {
    setViewing(fileId);
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {}
    setViewing("");
  };

  return (
    <div>
      <PageHeader index="03" eyebrow="Compliance" title="Documents & compliance" tag="Provincial · Ontario" />

      <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
        <ShieldCheck className="h-4 w-4 text-orange-500" /> Keep every document current to stay active on the network.
      </div>

      <div className="border border-gray-200 divide-y divide-gray-200 mb-8" data-testid="compliance-list">
        <ExpiryRow label="Driver's licence" value={`${c.licence?.class || "—"} · ${c.licence?.number || ""}`} expiry={c.licence?.expiry} />
        <ExpiryRow label="Insurance" value={c.insurance?.provider ? `${c.insurance.provider} · ${c.insurance.policy}` : "—"} expiry={c.insurance?.expiry} />
        <ExpiryRow label="Vehicle" value={c.vehicle ? `${c.vehicle.year} ${c.vehicle.make} ${c.vehicle.model} · ${c.vehicle.plate}` : "—"} expiry={null} />
      </div>

      <div className="flex items-center gap-4 mb-4">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Uploaded files</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>
      <div className="border border-gray-200 divide-y divide-gray-200">
        {(!c.documents || c.documents.length === 0) && <div className="px-5 py-8 text-center text-sm text-gray-400">No documents on file.</div>}
        {(c.documents || []).map((doc) => (
          <div key={doc.file_id} className="flex items-center justify-between px-5 py-3.5">
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-gray-400" />
              <div>
                <div className="text-sm text-[#0a0a0a] capitalize">{doc.type}</div>
                <div className="text-xs text-gray-500">{doc.filename}</div>
              </div>
            </div>
            <button onClick={() => viewDoc(doc.file_id)} data-testid={`view-doc-${doc.file_id}`} className="font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 hover:underline">
              {viewing === doc.file_id ? "Opening…" : "View"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
